import { NgComponentOutlet } from '@angular/common';
import {
  Component,
  effect,
  forwardRef,
  inject,
  input,
  linkedSignal,
  output,
  untracked,
} from '@angular/core';
import {
  TABLE_GET_DETAILS,
  TABLE_IDENTIFY_BY,
  TableStateService,
  type GetDetailsOptions,
} from '@mb/components';
import { clientLogger, isAbortError } from '@mb/common/client';
import type { IPerson } from '../../shared';
import { ContactsApiService } from '../services/contacts-api.service';
import {
  PERSON_ADMIN_FLOW,
  PERSON_EDIT_FORM,
  type PersonAdminFlow,
} from './person-admin.flow';
import { PersonDetailsComponent } from './person-details.component';

/**
 * The persons page's details drawer, for a person picked anywhere else.
 *
 * `PersonDetailsComponent` reads its person from the table state the persons
 * page provides. Rather than teach it a second way in, this gives it a table
 * state of its own with that one person selected — so it loads, and looks,
 * exactly as it does under Persons, and whatever is added there shows here.
 *
 * It is also the drawer's flow: Edit turns it into the form the host gave
 * as `PERSON_EDIT_FORM`, saving or cancelling turns it back, and nothing
 * navigates. What the host has to know — that a person changed or is gone —
 * comes out as `personSaved` and `personDeleted`.
 *
 * The host provides `PERSON_EDIT_FORM` and, for extra tabs,
 * `PERSON_DETAIL_TABS`.
 */
@Component({
  selector: 'contacts-person-preview',
  standalone: true,
  imports: [PersonDetailsComponent, NgComponentOutlet],
  host: { class: 'flex min-h-0 flex-1 flex-col overflow-hidden' },
  providers: [
    TableStateService<IPerson>,
    { provide: TABLE_IDENTIFY_BY, useValue: (person: IPerson) => person.id },
    {
      provide: TABLE_GET_DETAILS,
      useFactory: () => {
        const api = inject(ContactsApiService);
        return async (id: string, options?: GetDetailsOptions) => {
          try {
            const result = await api.getPerson(id, { signal: options?.signal });
            return result.data;
          } catch (error) {
            if (!isAbortError(error)) {
              clientLogger.error('Failed to load person preview', {
                error: error instanceof Error ? error : new Error(String(error)),
              });
            }
            return null;
          }
        };
      },
    },
    {
      provide: PERSON_ADMIN_FLOW,
      useExisting: forwardRef(() => PersonPreviewComponent),
    },
  ],
  template: `
    @if (editing()) {
      <ng-container
        *ngComponentOutlet="editForm; inputs: { personId: personId() }"
      />
    } @else {
      <workforce-person-details />
    }
  `,
})
export class PersonPreviewComponent implements PersonAdminFlow {
  readonly #tableState = inject(TableStateService<IPerson>);
  protected readonly editForm = inject(PERSON_EDIT_FORM);

  readonly personId = input.required<string>();
  /** Translation key for the delete button; the drawer's own when unset. */
  readonly deleteLabel = input<string | undefined>();

  /** The person was saved from the form — the host may show their name. */
  readonly personSaved = output<string>();
  /** The person was deleted — the host has nothing left to show. */
  readonly personDeleted = output<string>();

  /** Another person picked while the form is open opens on their details. */
  protected readonly editing = linkedSignal({
    source: this.personId,
    computation: () => false,
  });

  constructor() {
    effect(() => {
      const id = this.personId();
      untracked(() => void this.#tableState.selectById(id));
    });
  }

  edit(): void {
    this.editing.set(true);
  }

  async saved(personId: string): Promise<void> {
    this.editing.set(false);
    this.personSaved.emit(personId);
  }

  cancelled(): void {
    this.editing.set(false);
  }

  async deleted(personId: string): Promise<void> {
    this.personDeleted.emit(personId);
  }
}
